// NPM Imports
import axios from "axios";
import axiosRetry from "axios-retry";
import * as Sentry from "@sentry/browser";

// Methods Imports
import { getRedirectUrlPostSignout } from "../actions/AuthActions/authMethods";

// Internal Imports
import { GlobalUtils } from "../utils";
import { API_ENDPOINTS } from "../constants";
import store from "../store";

// Destructuring
const { APIS } = API_ENDPOINTS;
const {
  readDataFromLocalStorage,
  removeDataFromLocalStorage,
  writeDataToLocalStorage,
  setUserCookies,
  removeUserCookies,
  readCookie,
} = GlobalUtils;

// Declarations
let fetchTokenPromise = null;
let PLAN_HASH = readDataFromLocalStorage("planHash") || null;

const removeAccessToken = () => {
  removeDataFromLocalStorage("revvsalesUser");
  removeUserCookies();
  window.location.href = getRedirectUrlPostSignout();
};

const makeCommonHeader = () => {
  const obj = {};
  obj["Content-Type"] = "application/json";
  const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
  const SF_SAML_ORG_ID = readDataFromLocalStorage("SFSAMLOrgID"); // In case of SF SAML Auth Request
  const SIGNUP_ORG_ID = readDataFromLocalStorage("signupOrgID"); // In case of Invute Signup
  const accessToken = readCookie("accessToken");

  if (process.env.REACT_APP_VERSION) {
    obj.feAppVersion = process.env.REACT_APP_VERSION;
  }

  // Set the header if user is authenticated
  if (CURRENT_USER) {
    if (accessToken) {
      obj.AccessToken = accessToken;
    }
    if (CURRENT_USER.org_id) {
      obj.AccNo = CURRENT_USER.org_id;
      obj.orgID = CURRENT_USER.org_id;
    }
  }
  // Set the header if user is not authenticated
  // ex: for signup purpose or SFSAML Auth Request
  else if (SIGNUP_ORG_ID) {
    obj.AccNo = SIGNUP_ORG_ID;
    obj.orgID = SIGNUP_ORG_ID;
  } else if (SF_SAML_ORG_ID) {
    obj.AccNo = SF_SAML_ORG_ID;
    obj.orgID = SF_SAML_ORG_ID;
  }
  return obj;
};

const triggerSentryError = ({ error, level, tag }) => {
  Sentry.configureScope(scope => {
    const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
    scope.setLevel(level);
    scope.setTag("custom_tag", tag);
    scope.setExtra(
      "user-email",
      (CURRENT_USER && CURRENT_USER.user_email) || "",
    );
    scope.setExtra("app_version", process.env.REACT_APP_VERSION);
    scope.setExtra(
      "incident-id",
      (error.response &&
        error.response.data &&
        error.response.data.incident_id) ||
        "",
    );
    scope.setExtra("payload", JSON.stringify(error));
    Sentry.captureException(error);
  });
};

/**
 * @param {string} method - Holds the http request methods like get,put,etc.
 * @param {string} url - API url to hit.
 * @param {object} headers - Holds header params for the API.
 * @param {object} requestBody - Holds the payload for the API.
 * @param {string} responseType - Holds request response type, i.e for export API the response type should be "arraybuffer" to handle the response.
 * @param {boolean} returnFullResponse - A flag to decide wheather full response should be sent or only the response.data.
 */

const makeApiCall = (
  method,
  url,
  headers,
  requestBody,
  responseType,
  returnFullResponse,
) => {
  const request = {
    method,
    url,
    headers,
    data: requestBody,
  };
  if (responseType) request.responseType = responseType;

  axiosRetry(axios, {
    retries: 2,
    retryDelay: retryCount => retryCount * 1000,
    retryCondition: err => {
      if (
        !err.response ||
        err.code === "ECONNABORTED" ||
        (err.response && err.response.status === 0)
      ) {
        console.error("axios retry attempt =>", err.config["axios-retry"]);
        return true;
      }
      return false;
    },
  });

  return axios(request)
    .then(response => {
      // * METHOD TO PERFORM ACTIONS BASED ON API HEADERS
      // eslint-disable-next-line no-use-before-define
      _interceptApiHeaders(response.headers);

      // * RETURN API RESPONSE
      if (returnFullResponse) return response;
      return response.data;
    })
    .catch(error => {
      if (error.response && error.response.status === 401) {
        const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
        if (CURRENT_USER) {
          if (error.config.headers.GrantType === "refreshToken") {
            removeAccessToken();
            throw error;
          } else {
            // eslint-disable-next-line no-use-before-define
            return retry(
              method,
              url,
              requestBody,
              responseType,
              returnFullResponse,
            );
          }
        } else {
          throw error;
        }
      } else if (
        error.response &&
        error.response.status !== 401 &&
        error.config.headers.GrantType === "refreshToken"
      ) {
        removeAccessToken();
        throw error;
      } else if (
        !error.response ||
        error.code === "ECONNABORTED" ||
        error.response.status === 0
      ) {
        triggerSentryError({ error, level: "info", tag: "Network Error" });
        throw error;
      }
      triggerSentryError({ error, level: "critical", tag: "API Error" });
      console.error("axios error", error);
      throw error;
    });
};

const deleteApi = (url, requestBody) => {
  const method = "delete";
  const headers = makeCommonHeader();
  return makeApiCall(method, url, headers, requestBody);
};

const get = (url, returnFullResponse) => {
  const method = "get";
  const headers = makeCommonHeader();
  return makeApiCall(method, url, headers, null, null, returnFullResponse);
};

const patch = (url, requestBody, returnFullResponse) => {
  const method = "patch";
  const headers = makeCommonHeader();
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    null,
    returnFullResponse,
  );
};

const post = (
  url,
  requestBody,
  GrantType,
  refreshToken,
  returnFullResponse,
) => {
  const method = "post";
  const headers = makeCommonHeader();
  // For Login we need to additional header params
  if (GrantType) {
    headers.GrantType = GrantType;
    headers.RefreshToken = refreshToken;
  }
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    null,
    returnFullResponse,
  );
};

const getForFileAsResponse = (url, responseType, returnFullResponse) => {
  const method = "get";
  const headers = makeCommonHeader();
  return makeApiCall(
    method,
    url,
    headers,
    null,
    responseType || "arraybuffer",
    returnFullResponse,
  );
};

const postForFileAsResponse = (
  url,
  requestBody,
  GrantType,
  refreshToken,
  returnFullResponse,
) => {
  const method = "post";
  const headers = makeCommonHeader();
  // For Login we need to additional header params
  if (GrantType) {
    headers.GrantType = GrantType;
    headers.RefreshToken = refreshToken;
  }
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    "arraybuffer",
    returnFullResponse,
  );
};

const uploadFile = (
  url,
  requestBody,
  GrantType,
  refreshToken,
  returnFullResponse,
) => {
  const method = "post";
  const headers = makeCommonHeader();
  headers["Content-Type"] = "multipart/form-data";
  // For Login we need to additional header params
  if (GrantType) {
    headers.GrantType = GrantType;
    headers.RefreshToken = refreshToken;
  }
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    null,
    returnFullResponse,
  );
};

const postForm = (url, requestBody, returnFullResponse) => {
  const method = "post";
  const headers = makeCommonHeader();
  headers["Content-Type"] = "multipart/form-data";
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    null,
    returnFullResponse,
  );
};

const put = (url, requestBody, returnFullResponse) => {
  const method = "put";
  const headers = makeCommonHeader();
  return makeApiCall(
    method,
    url,
    headers,
    requestBody,
    null,
    returnFullResponse,
  );
};

const getTokens = () => {
  const url = `${APIS.AUTH}/initiate-auth`;
  const user = readDataFromLocalStorage("revvsalesUser");
  const refreshToken = readCookie("refreshToken");
  return post(url, null, "refreshToken", refreshToken)
    .then(res => {
      const { access_token: newAccessToken, refresh_token: newRefreshToken } =
        res.User;
      user.access_token = newAccessToken;
      user.refresh_token = newRefreshToken;
      setUserCookies(user);
      return res.User;
    })
    .catch(err => {
      console.debug(err);
    });
};

const retry = (method, url, requestBody, responseType, returnFullResponse) => {
  if (!fetchTokenPromise) {
    fetchTokenPromise = getTokens();
  }
  return fetchTokenPromise
    .then(() => {
      fetchTokenPromise = null;
      const headers = makeCommonHeader();
      return makeApiCall(
        method,
        url,
        headers,
        requestBody,
        responseType,
        returnFullResponse,
      );
    })
    .catch(err => {
      fetchTokenPromise = null;
      throw err;
    });
};

const _monitorBillingPlanChange = async headers => {
  if (!PLAN_HASH) {
    writeDataToLocalStorage("planHash", (headers && headers.plan_hash) || "");
    PLAN_HASH = readDataFromLocalStorage("planHash");
    return true;
  }

  if (PLAN_HASH !== headers.plan_hash) {
    // Update the new plan hash to the const key
    writeDataToLocalStorage("planHash", (headers && headers.plan_hash) || "");
    PLAN_HASH = readDataFromLocalStorage("planHash");

    try {
      const userFeatures = await get(`${APIS.USER_FEATURES}`);
      const userSignUpInfo = await get(`${APIS.AUTH}/signup_info`);
      const billingInfo = await get(`${APIS.BILLING_INFO}`);
      const promiseArray = await Promise.all([
        userFeatures,
        userSignUpInfo,
        billingInfo,
      ])
        .then(res => {
          writeDataToLocalStorage("revvsalesUserFeatures", res[0].UserFeatures);
          writeDataToLocalStorage("userSignUpInfo", res[1].SignupInfo);
          writeDataToLocalStorage(
            "revvsalesUserPlanType",
            res[2].billing_details.plan_type,
          );
          // Update billing related info
          writeDataToLocalStorage(
            "revv_planStatus",
            res[2] &&
              res[2].billing_details &&
              res[2].billing_details.subscription &&
              res[2].billing_details.subscription.status,
          );
          store.dispatch({
            type: "FETCH_BILLING_INFO",
            payload: (res[2] && res[2].billing_details) || {},
          });
        })
        .catch(error => {
          console.error("api/index.js => _monitorBillingPlanChange", error);
        });
      return promiseArray;
    } catch (error) {
      console.error(`api/index.js => _monitorBillingPlanChange => ${error}`);
      throw error;
    }
  }
  return true;
};

const _monitorAppVersionBump = headers => {
  if (!window.appVersion) {
    window.appVersion = headers.fe_version || null;
    return true;
  }

  if (headers.fe_version && window.appVersion !== headers.fe_version) {
    // Check if its a major release
    const MAJOR_VERSION_IN_APP = window.appVersion.split(".")[0];
    const MAJOR_VERSION_IN_API = headers.fe_version.split(".")[0];
    const isMajorRelease = MAJOR_VERSION_IN_API > MAJOR_VERSION_IN_APP;

    // Save a referece of the old app version
    const oldVersion = window.appVersion;

    // Update the new app version to the const key
    window.appVersion = headers.fe_version;

    // DISPATCH A REDUX EVENT TO DISPLAY THE NEW APP NOTIFICATION COMPONENT
    store.dispatch({
      type: "TRIGGER_NEW_APP_NOTIFICATION",
      payload: {
        newVersionFound: true,
        isMajorRelease,
        oldVersion,
        newVersion: window.appVersion,
      },
    });
    return true;
  }
  return true;
};

const _interceptApiHeaders = headers => {
  _monitorBillingPlanChange(headers);
  _monitorAppVersionBump(headers);
};

export {
  deleteApi,
  get,
  patch,
  post,
  put,
  postForm,
  uploadFile,
  postForFileAsResponse,
  getForFileAsResponse,
};
